import { useCallback, useState } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { uploadProfilePhoto } from '../lib/profilePhotoStorage';

const MAX_BYTES = 5 * 1024 * 1024;

interface ProfilePhotoUpload {
  uploading: boolean;
  error: string | null;
  upload: (file: File) => Promise<void>;
}

export function useProfilePhotoUpload(userId: string | undefined): ProfilePhotoUpload {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upload = useCallback(
    async (file: File) => {
      if (!userId) return;
      if (!file.type.startsWith('image/')) {
        setError('Please choose an image file.');
        return;
      }
      if (file.size > MAX_BYTES) {
        setError('Image must be smaller than 5 MB.');
        return;
      }
      setError(null);
      setUploading(true);
      try {
        const url = await uploadProfilePhoto(userId, file);
        await setDoc(doc(db, 'users', userId), { avatar: url }, { merge: true });
      } catch (e) {
        console.error('Profile photo upload failed', e);
        setError('Upload failed. Please try again.');
      } finally {
        setUploading(false);
      }
    },
    [userId]
  );

  return { uploading, error, upload };
}
